import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {behead, untail} from '../commons/commons';
import {APP_CONFIG_TOKEN, AppConfig} from '../app.config';
import {OnboardingState, OnboardingUser, SmsTotp} from 'smag-commons/dto/api/onboarding';
import {
    ConsumerPodStatisticalProfileRequest,
    CustomerConfig,
    ProducerPodStatisticalProfileRequest,
    ProsumerPodStatisticalProfileRequest
} from 'smag-commons/dto/api';
import {ECDocument, PodStatisticalProfile} from 'smag-commons/cfg/hierarchy';
import {EconomicsConfig} from '../views/onboarding/onboarding.component';

export interface OnboardingFile {
    filename: string;
    content: Blob;
}

export interface PasswordStrengthMessage {
    message: string;
    argument: string;
}

export interface PasswordStrength {
    quality: number;
    messages: PasswordStrengthMessage[];
    valid?: boolean;
}

interface PasswordStrengthRequest {
    password: string;
}

const API_PREFIX = '';

/**
 * Api service that deals with (TOTP-authenticated) "onboarding" API of api-server
 */
@Injectable()
export class ApiService {

    constructor(private readonly http: HttpClient,
                @Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig) {
    }

    getMember(totp: string): Observable<OnboardingState> {
        return this.http.get(mkUrl(this.cfg, `${API_PREFIX}/onboarding/${totp}`))
            .pipe(map(json => json as OnboardingState));
    }

    onboardUser(totp: string, user: OnboardingUser, files?: { [partId: string]: OnboardingFile }): Promise<void> {
        const formData = new FormData();
        formData.append('user', JSON.stringify(user));
        Object.keys(files || {}).forEach(partId => {
            formData.append(partId, files[partId].content, files[partId].filename);
        });
        return this.http.request('post', mkUrl(this.cfg, `${API_PREFIX}/onboarding/${totp}`), {
            body: formData,
            headers: new HttpHeaders(),
            responseType: 'json'
        })
            .pipe(map(resp => resp as undefined))
            .toPromise();
    }

    inviteMember(totp: string): Promise<void> {
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/onboarding/invite/${totp}`), undefined)
            .pipe(map(resp => resp as undefined))
            .toPromise();
    }

    getCustomerConfig(totp: string): Observable<CustomerConfig> {
        return this.http.get(mkUrl(this.cfg, `${API_PREFIX}/onboarding/customer-config/${totp}`))
            .pipe(map(json => json as CustomerConfig));
    }

    getECDocument(totp: string, documentId: string): Promise<Blob> {
        const url = `${API_PREFIX}/onboarding/document/${totp}`;
        return this.http.get(mkUrl(this.cfg, url),
            {params: {id: documentId}, responseType: 'blob'})
            .toPromise();
    }

    getECDocuments(totp: string): Observable<ECDocument[]> {
        return this.http.get(mkUrl(this.cfg, `${API_PREFIX}/onboarding/documents/${totp}`))
            .pipe(map(json => json as ECDocument[]));
    }

    sendSmsTotp(totp: string): Promise<void> {
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/onboarding/sms-totp/${totp}`), undefined)
            .pipe(map(resp => resp as undefined))
            .toPromise();
    }

    verifySmsTotp(totp: string, smsTotp: SmsTotp): Promise<void> {
        return this.http.put(mkUrl(this.cfg, `${API_PREFIX}/onboarding/sms-totp/${totp}`), smsTotp)
            .pipe(map(resp => resp as undefined))
            .toPromise();
    }

    measurePasswordStrength(password: string): Promise<PasswordStrength> {
        const req: PasswordStrengthRequest = {password};
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/password/strength`), req)
            .pipe(map(json => json as PasswordStrength))
            .toPromise();
    }

    getConsumerPodStatisticalProfile(req: ConsumerPodStatisticalProfileRequest, totp: string): Promise<PodStatisticalProfile> {
        const params = new HttpParams().set('totp', totp);
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/onboarding/profiles/pod/consumer`), req, {params})
            .pipe(map(resp => resp as PodStatisticalProfile))
            .toPromise();
    }

    getProsumerPodStatisticalProfile(req: ProsumerPodStatisticalProfileRequest, totp: string): Promise<PodStatisticalProfile> {
        const params = new HttpParams().set('totp', totp);
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/onboarding/profiles/pod/prosumer`), req, {params})
            .pipe(map(resp => resp as PodStatisticalProfile))
            .toPromise();
    }

    getProducerPodStatisticalProfile(req: ProducerPodStatisticalProfileRequest, totp: string): Promise<PodStatisticalProfile> {
        const params = new HttpParams().set('totp', totp);
        return this.http.post(mkUrl(this.cfg, `${API_PREFIX}/onboarding/profiles/pod/producer`), req, {params})
            .pipe(map(resp => resp as PodStatisticalProfile))
            .toPromise();
    }

    getEconomicsConfig(totp: string): Promise<EconomicsConfig> {
        const params = new HttpParams().set('totp', totp);
        return this.http.get(mkUrl(this.cfg, `${API_PREFIX}/onboarding/economics-config`), {params})
            .pipe(map((resp: any) => {
                return {
                    residential_consumption_profile_templates: resp.residential_consumption_profile_templates,
                    non_residential_consumption_profile_template: resp.non_residential_consumption_profile_templates,
                } as EconomicsConfig;
            }))
            .toPromise();
    }
}

/**
 * Prepares the API endpoint URL.
 *
 * @param cfg Auth configuration
 * @param path The relative path to the exact endpoint
 * @return string The endpoint URL string
 */
function mkUrl(cfg: AppConfig, path: string): string {
    return mkUrlOrigin(cfg.domainPrefix, mkUrlPath(cfg.rootPath, path));
}

function mkUrlPath(rootPath: string, path: string): string {
    return rootPath ? untail(rootPath, '/') + '/' + behead(path, '/') : path;
}

function mkUrlOrigin(domainPrefix: string, path: string): string {
    const url = new URL(location.origin);
    url.pathname = path;
    url.hostname = `${domainPrefix || ''}${url.hostname}`;
    return url.href;
}
